import { FC } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { observer } from 'mobx-react-lite';

import { TooltipCard } from '@components';
import { useTrans } from '@libs/i18n';
import { CheckCircleIcon, WarningIcon } from '@phosphor-icons/react';

import { useProfileSummary } from '../api';

import { COMPLETION_HEALTHY_THRESHOLD, completionTone } from './profileNudge.utils';

/**
 * Compact completion-score pill for the header's Organization Hub button:
 * shows the live completion % with a tone-coloured alarm icon while the
 * profile is incomplete, and a success check once it is healthy.
 */
export const ProfileCompletionBadge: FC = observer(() => {
  const theme = useTheme();
  const { t } = useTrans();
  const { data } = useProfileSummary();

  if (!data) return null;

  const pct = data.progressPct;
  const color = theme.palette[completionTone(pct)].main;
  const healthy = pct >= COMPLETION_HEALTHY_THRESHOLD;

  return (
    <TooltipCard
      variant="neutral"
      size="sm"
      description={
        healthy
          ? t('app.orgProfile.nudge.badge.completeTooltip')
          : t('app.orgProfile.nudge.badge.tooltip', { percent: pct })
      }
    >
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          px: 1,
          py: 0.25,
          borderRadius: 999,
          backgroundColor: alpha(color, 0.12),
        }}
      >
        {healthy ? (
          <CheckCircleIcon size={13} color={color} weight="fill" />
        ) : (
          <WarningIcon size={13} color={color} weight="fill" />
        )}
        <Typography variant="caption" sx={{ fontWeight: 600, color }}>
          {pct}%
        </Typography>
      </Box>
    </TooltipCard>
  );
});
